<template>
  <div>
    <jet-banner />

    <div class="min-h-screen bg-gray-100">
      <nav class="bg-white border-b border-gray-100">
        <!-- Primary Navigation Menu -->
        <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div class="flex justify-between h-16">
            <div class="flex">
              <!-- Logo -->
              <div class="flex items-center flex-shrink-0">
                <a href="/">
                  <jet-application-mark class="block w-auto h-9" />
                </a>
              </div>

              <!-- Navigation Links -->
              <div class="hidden space-x-8 sm:flex sm:items-center sm:-my-px sm:ml-10">
                <component :is="nl.children ? 'jet-dropdown' : 'jet-nav-link'" v-for="nl in navLinks" :key="nl.path" :href="nl.path || false" :active="$page.url == nl.path">
                  <template v-if="nl.children" #trigger>
                    <dropdown-trigger>{{ nl.label }}</dropdown-trigger>
                  </template>
                  <template v-if="nl.children" #content>
                    <jet-dropdown-link v-for="dropdownItem in nl.children" :key="dropdownItem.path" :href="dropdownItem.path" :active="dropdownItem.path == $page.url">
                      <span v-if="dropdownItem.badge" class="inline-flex items-center justify-center float-right px-2 py-1 text-xs font-bold leading-none text-gray-100 bg-gray-500 rounded-full">{{ dropdownItem.badge }}</span>
                      {{ dropdownItem.label }}
                    </jet-dropdown-link>
                  </template>
                  <template v-if="nl.path">
                    {{ nl.label }}
                  </template>
                </component>
              </div>
            </div>

            <div class="hidden sm:flex sm:items-center sm:ml-6">
              <div class="relative ml-3">
                <notifications-dropdown />
              </div>
              <div class="relative ml-3">
                <!-- Teams Dropdown -->
                <jet-dropdown v-if="$page.props.jetstream.hasTeamFeatures" align="right" width="60">
                  <template #trigger>
                    <span class="inline-flex rounded-md">
                      <button type="button" class="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out bg-white border border-transparent rounded-md hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50">
                        {{ $page.props.user.current_team.name }}

                        <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  </template>

                  <template #content>
                    <div class="w-60">
                      <!-- Team Management -->
                      <template v-if="$page.props.jetstream.hasTeamFeatures">
                        <div class="block px-4 py-2 text-xs text-gray-400">
                          Manage Team
                        </div>

                        <!-- Team Settings -->
                        <jet-dropdown-link :href="route('teams.show', $page.props.user.current_team)">
                          Team Settings
                        </jet-dropdown-link>

                        <jet-dropdown-link v-if="$page.props.jetstream.canCreateTeams" :href="route('teams.create')">
                          Create New Team
                        </jet-dropdown-link>

                        <div class="border-t border-gray-100" />

                        <!-- Team Switcher -->
                        <div class="block px-4 py-2 text-xs text-gray-400">
                          Switch Teams
                        </div>

                        <template v-for="team in $page.props.user.all_teams">
                          <form :key="team.id" @submit.prevent="switchToTeam(team)">
                            <jet-dropdown-link as="button">
                              <div class="flex items-center">
                                <svg v-if="team.id == $page.props.user.current_team_id" class="w-5 h-5 mr-2 text-green-400" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <div>{{ team.name }}</div>
                              </div>
                            </jet-dropdown-link>
                          </form>
                        </template>
                      </template>
                    </div>
                  </template>
                </jet-dropdown>
              </div>

              <!-- Settings Dropdown -->
              <div class="relative ml-3">
                <jet-dropdown align="right" width="48">
                  <template #trigger>
                    <button v-if="$page.props.jetstream.managesProfilePhotos" class="flex text-sm transition duration-150 ease-in-out border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300">
                      <img class="object-cover w-8 h-8 rounded-full" :src="$page.props.user.profile_photo_url" :alt="$page.props.user.name">
                    </button>

                    <span v-else class="inline-flex rounded-md">
                      <button type="button" class="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out bg-white border border-transparent rounded-md hover:text-gray-700 focus:outline-none">
                        {{ $page.props.user.name }}

                        <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  </template>

                  <template #content>
                    <!-- Account Management -->
                    <div class="block px-4 py-2 text-xs text-gray-400">
                      Manage Account
                    </div>

                    <jet-dropdown-link :href="route('profile.show')">
                      Profile
                    </jet-dropdown-link>

                    <jet-dropdown-link v-if="$page.props.jetstream.hasApiFeatures" :href="route('api-tokens.index')">
                      API Tokens
                    </jet-dropdown-link>

                    <div class="border-t border-gray-100" />

                    <!-- Authentication -->
                    <jet-dropdown-link href="/logout">
                      Logout
                    </jet-dropdown-link>
                  </template>
                </jet-dropdown>
              </div>
            </div>

            <!-- Hamburger -->
            <div class="flex items-center -mr-2 sm:hidden">
              <button class="inline-flex items-center justify-center p-2 text-gray-400 transition duration-150 ease-in-out rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500" @click="showingNavigationDropdown = ! showingNavigationDropdown">
                <svg class="w-6 h-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Responsive Navigation Menu -->
        <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden">
          <div class="pt-2 pb-3 space-y-1">
            <component
              :is="nl.children ? 'div' : 'jet-responsive-nav-link'"
              v-for="nl in navLinks"
              :key="nl.path"
              :class="{ 'pt-4 pb-1 border-t border-b border-gray-200': nl.children }"
              :href="nl.path || false"
            >
              <template v-if="nl.path">
                {{ nl.label }}
              </template>
              <template v-else-if="nl.children">
                <div v-if="nl.children" class="flex items-center px-4">
                  <div class="text-base font-medium text-gray-800">{{ nl.label }}</div>
                </div>
                <div v-if="nl.children" class="mt-3 space-y-1">
                  <jet-responsive-nav-link v-for="child in nl.children" :key="child.path" :href="child.path" :active="$page.url == child.path">
                    <span v-if="child.badge" class="inline-flex items-center justify-center float-right px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">{{ child.badge }}</span>
                    {{ child.label }}
                  </jet-responsive-nav-link>
                </div>
              </template>
            </component>
          </div>

          <!-- Responsive Settings Options -->
          <div class="pt-4 pb-1 border-t border-gray-200">
            <div class="flex items-center px-4">
              <div v-if="$page.props.jetstream.managesProfilePhotos" class="flex-shrink-0 mr-3">
                <img class="object-cover w-10 h-10 rounded-full" :src="$page.props.user.profile_photo_url" :alt="$page.props.user.name">
              </div>

              <div>
                <div class="text-base font-medium text-gray-800">{{ $page.props.user.name }}</div>
                <div class="text-sm font-medium text-gray-500">{{ $page.props.user.email }}</div>
              </div>
            </div>

            <div class="mt-3 space-y-1">
              <jet-responsive-nav-link :href="route('profile.show')" :active="route().current('profile.show')">
                Profile
              </jet-responsive-nav-link>

              <jet-responsive-nav-link v-if="$page.props.jetstream.hasApiFeatures" :href="route('api-tokens.index')" :active="route().current('api-tokens.index')">
                API Tokens
              </jet-responsive-nav-link>

              <!-- Authentication -->
              <jet-responsive-nav-link href="/logout">
                Logout
              </jet-responsive-nav-link>

              <!-- Team Management -->
              <template v-if="$page.props.jetstream.hasTeamFeatures">
                <div class="border-t border-gray-200" />

                <div class="block px-4 py-2 text-xs text-gray-400">
                  Manage Team
                </div>

                <!-- Team Settings -->
                <jet-responsive-nav-link :href="route('teams.show', $page.props.user.current_team)" :active="route().current('teams.show')">
                  Team Settings
                </jet-responsive-nav-link>

                <jet-responsive-nav-link :href="route('teams.create')" :active="route().current('teams.create')">
                  Create New Team
                </jet-responsive-nav-link>

                <div class="border-t border-gray-200" />

                <!-- Team Switcher -->
                <div class="block px-4 py-2 text-xs text-gray-400">
                  Switch Teams
                </div>

                <template v-for="team in $page.props.user.all_teams">
                  <form :key="team.id" @submit.prevent="switchToTeam(team)">
                    <jet-responsive-nav-link as="button">
                      <div class="flex items-center">
                        <svg v-if="team.id == $page.props.user.current_team_id" class="w-5 h-5 mr-2 text-green-400" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>{{ team.name }}</div>
                      </div>
                    </jet-responsive-nav-link>
                  </form>
                </template>
              </template>
            </div>
          </div>
        </div>
      </nav>

      <!-- Page Heading -->
      <header v-if="$slots.header" class="bg-white shadow">
        <div class="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <slot name="header" />
        </div>
      </header>

      <!-- Page Content -->
      <main>
        <slot />
      </main>

      <!-- Modal Portal -->
      <portal-target name="modal" multiple />
      <portal-target name="notification" multiple />

      <center class="pb-4 text-xs">Designed and Developed by <a href="https://magis.marketing/" class="underline hover:no-underline blue-text">Magis Solutions</a></center>
    </div>
  </div>
</template>

<script>
import JetApplicationMark from '@/Jetstream/ApplicationMark'
import JetBanner from '@/Jetstream/Banner'
import JetDropdown from '@/Jetstream/Dropdown'
import JetDropdownLink from '@/Jetstream/DropdownLink'
import JetNavLink from '@/Jetstream/NavLink'
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink'
import DropdownTrigger from '@/Components/Navigation/DropdownTrigger'
import DropdownTriggerMobile from '@/Components/Navigation/DropdownTriggerMobile'
import NotificationsDropdown from '@/Magis/Components/NotificationsDropdown'

export default {
  components: {
    JetApplicationMark,
    JetBanner,
    JetDropdown,
    JetDropdownLink,
    JetNavLink,
    JetResponsiveNavLink,
    DropdownTrigger,
    DropdownTriggerMobile,
    NotificationsDropdown,
  },

  data () {
    return {
      showingNavigationDropdown: false,
    }
  },

  computed: {
    user () {
      return this.$page.props.user
    },

    navLinks () {
      const user = this.user
      if (! user.is_editor && ! user.is_technical) {
        return [
          {
            path: '/my_submissions',
            label: 'My Submissions',
          },
          {
            path: '/review',
            label: 'For Review',
          },
        ]
      }

      let navLinks = []

      if (user.is_editor || user.is_technical) {
        navLinks.push({
          path: '/issues/admin',
          label: 'Issues',
        })
      }

      if (user.is_editor && window.SUBMISSION_STATUSES) {
        navLinks.push({
          label: 'Submissions',
          children: window.SUBMISSION_STATUSES.map(submissionStatus => ({
            path: `/submissions/admin/${submissionStatus.slug}`,
            label: submissionStatus.name,
            badge: submissionStatus.submitted_submissions_count,
          })),
        })
      }

      if (user.is_technical && window.PAGE_TYPES) {
        navLinks = navLinks.concat([
          {
            label: 'Pages',
            children: window.PAGE_TYPES.map(pageType => ({
              path: `/pages/admin/${pageType.slug}`,
              label: pageType.name,
            })),
          },
          {
            path: '/submission_requirements/admin',
            label: 'Requirements',
          },
          {
            path: '/admins',
            label: 'Admins',
          },
        ])
      }

      return navLinks
    },
  },

  methods: {
    switchToTeam (team) {
      this.$inertia.put(window.route('current-team.update'), {
        team_id: team.id,
      }, {
        preserveState: false,
      })
    },
  },
}
</script>
